
import {computed, defineComponent, onMounted, ref} from 'vue';
import {APIService} from '@/core/util/services/api.service';

type PayslipItem = {name: string; type: string; amount: string};
type Payslip = {
  id: number;
  employee_id: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  department: string | null;
  designation: string | null;
  period_start: string;
  period_end: string;
  gross: string;
  total_deductions: string;
  net: string;
  sales_revenue_usd?: string;
  usd_pkr_rate?: string | null;
  commission_rate?: number;
  bank_name?: string | null;
  bank_account?: string | null;
  payment_mode?: string | null;
  currency?: string;
  footer?: string;
  items: PayslipItem[];
};

export default defineComponent({
  name: 'MyPayslips',
  setup() {
    const baseUrl = (window as unknown as {appGlobal: {baseUrl: string}})
      .appGlobal.baseUrl;
    const staticBaseUrl = baseUrl.replace(/\/index\.php\/?$/, '');
    const connexixLogo = `${staticBaseUrl}/dist/connexix-logo.png`;
    const api = new APIService(baseUrl, '/api/v2/finance/payslips');
    const slips = ref<Payslip[]>([]);
    const selected = ref<Payslip | null>(null);
    const errorMessage = ref('');
    const companyName = 'Connexix';
    const fullName = computed(() =>
      [
        selected.value?.first_name,
        selected.value?.middle_name,
        selected.value?.last_name,
      ]
        .filter(Boolean)
        .join(' '),
    );
    const paymentMode = computed(() => {
      const mode = selected.value?.payment_mode || '';
      return mode
        ? mode
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (letter) => letter.toUpperCase())
        : '—';
    });
    const maskedAccount = computed(() =>
      selected.value?.bank_account
        ? `•••• ${selected.value.bank_account.slice(-4)}`
        : '—',
    );
    const earnings = computed(() =>
      (selected.value?.items || []).filter((item) => item.type === 'earning'),
    );
    const deductions = computed(() =>
      (selected.value?.items || []).filter((item) => item.type === 'deduction'),
    );
    const money = (value: string | number) =>
      Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    const period = (value: string) =>
      new Intl.DateTimeFormat(undefined, {
        month: 'long',
        year: 'numeric',
      }).format(new Date(`${value}T00:00:00`));
    const dateLabel = (value: string) =>
      new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
      }).format(new Date(`${value}T00:00:00`));
    const load = async () => {
      errorMessage.value = '';
      try {
        const response = await api.getAll();
        slips.value = response.data.data || [];
        if (slips.value.length) await open(slips.value[0]);
      } catch (error: any) {
        errorMessage.value =
          error?.response?.data?.error?.message || 'Could not load payslips.';
      }
    };
    const open = async (item: Payslip) => {
      try {
        const response = await api.get(Number(item.id));
        selected.value = response.data.data;
      } catch (error: any) {
        errorMessage.value =
          error?.response?.data?.error?.message || 'Could not open payslip.';
      }
    };
    const downloadSlip = async () => {
      if (!selected.value) return;
      const date = new Date(`${selected.value.period_start}T00:00:00`);
      const month = new Intl.DateTimeFormat('en-US', {month: 'long'}).format(
        date,
      );
      const year = date.getFullYear();
      const fileName = `${companyName}_${month}_${year}_Payslip`;
      const source = document.querySelector<HTMLElement>('.slip');
      if (!source) {
        errorMessage.value = 'Could not prepare the payslip for download.';
        return;
      }
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        errorMessage.value = 'Allow pop-ups to download the payslip PDF.';
        return;
      }

      const clone = source.cloneNode(true) as HTMLElement;
      const sourceElements = [
        source,
        ...Array.from(source.querySelectorAll('*')),
      ];
      const cloneElements = [clone, ...Array.from(clone.querySelectorAll('*'))];
      sourceElements.forEach((element, index) => {
        const styles = window.getComputedStyle(element);
        const inlineStyles = Array.from(styles)
          .map(
            (property) => `${property}:${styles.getPropertyValue(property)};`,
          )
          .join('');
        cloneElements[index].setAttribute('style', inlineStyles);
      });
      clone.style.width = '100%';
      clone.style.maxWidth = 'none';
      clone.style.margin = '0';
      clone.style.padding = '0';
      clone.style.border = '0';

      printWindow.document.open();
      printWindow.document.write(
        `<!doctype html><html><head><meta charset="utf-8"><title>${fileName}</title><style>@font-face{font-family:'Nunito Sans';font-style:normal;font-weight:400;src:url('${staticBaseUrl}/dist/fonts/nunito-sans-v6-latin-ext_latin-regular.woff2') format('woff2')}@font-face{font-family:'Nunito Sans';font-style:normal;font-weight:700;src:url('${staticBaseUrl}/dist/fonts/nunito-sans-v6-latin-ext_latin-700.woff2') format('woff2')}@page{size:A4 portrait;margin:15mm 20mm 16mm}html,body{margin:0;background:#fff}body{font-family:'Nunito Sans',Arial,sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact}.slip{box-sizing:border-box;width:100%;max-width:none;margin:0;padding:0;border:0;break-inside:avoid;page-break-inside:avoid}*{box-sizing:border-box}</style></head><body></body></html>`,
      );
      printWindow.document.body.appendChild(clone);
      printWindow.document.close();
      const images = Array.from(printWindow.document.images);
      await Promise.all(
        images.map((image) =>
          image.complete
            ? Promise.resolve()
            : new Promise<void>((resolve) => {
                image.onload = () => resolve();
                image.onerror = () => resolve();
              }),
        ),
      );
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
    onMounted(load);
    return {
      slips,
      selected,
      errorMessage,
      connexixLogo,
      fullName,
      paymentMode,
      maskedAccount,
      earnings,
      deductions,
      money,
      period,
      open,
      dateLabel,
      downloadSlip,
    };
  },
});
